import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCents } from '@/domain/money';
import type { CollectionBalance } from '@/domain/collectionEngine';

/**
 * The running total the agent watches while splitting money across invoices.
 *
 * It is sticky at the bottom on small screens because on a phone the invoice
 * list pushes it off-screen exactly when it matters most — the agent needs to
 * see "still to assign" while typing into the row that changes it.
 */
export function BalanceBar({ balance }: { balance: CollectionBalance }): JSX.Element {
  const { t } = useTranslation();

  const isOver = balance.remainingCents < 0;
  const isDone = balance.isBalanced;

  return (
    <div
      className={cn(
        'sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 shadow-lg',
        isDone
          ? 'border-brand-300 bg-brand-50'
          : isOver
            ? 'border-red-300 bg-red-50'
            : 'border-amber-300 bg-amber-50',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <div>
          <span className="text-slate-600">{t('collection.collected')}: </span>
          <span className="tabular font-semibold text-slate-900">
            {formatCents(balance.collectedCents)}
          </span>
        </div>

        <div>
          <span className="text-slate-600">{t('collection.assigned')}: </span>
          <span className="tabular font-semibold text-slate-900">
            {formatCents(balance.allocatedCents)}
          </span>
        </div>

        <div>
          <span className="text-slate-600">
            {isOver ? t('collection.overAssigned') : t('collection.remaining')}:{' '}
          </span>
          <span
            className={cn(
              'tabular font-bold',
              isDone ? 'text-brand-700' : isOver ? 'text-red-700' : 'text-amber-700',
            )}
          >
            {formatCents(Math.abs(balance.remainingCents))}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-sm font-medium">
        {isDone ? (
          <>
            <CheckCircle2 className="h-4 w-4 text-brand-600" />
            <span className="text-brand-700">{t('collection.balanced')}</span>
          </>
        ) : (
          <>
            <AlertCircle className={cn('h-4 w-4', isOver ? 'text-red-600' : 'text-amber-600')} />
            <span className={isOver ? 'text-red-700' : 'text-amber-700'}>
              {isOver ? t('collection.overAssigned') : t('collection.remaining')}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
